import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUserProjectTimeZone } from 'core/userContext';
import {
  FooterWrapper,
  TakeoverHeaderBarContent,
  WithdrawnDrawerTakeover,
} from 'modules/campaigns/editor/takeovers/Takeovers.styled';
import { EmbeddedDisplayUntil } from 'modules/domains/campaign/types';
import { BUTTON_VARIANTS, Button } from 'ui/button';
import { Body2 } from 'ui/theme';
import noop from 'utils/noop';
import AddItemButton from './AddItemButton';
import ConfirmationModal from './ConfirmationModal';
import { ButtonRow } from './DisplayUntil.styles';
import DisplayUntilItem from './DisplayUntilItem';
import { SEARCH_QUERY_STUB } from './DisplayUntilTakeover.constants';
import { getQueryErrors } from './DisplayUntilTakeover.utils';
import EndDateItem from './EndDateItem';
import PropertyChangeItem from './PropertyChangeItem';
import { formatInTimeZone } from 'date-fns-tz';
import { addHours } from 'date-fns';

interface DisplayUntilTakeoverProps {
  /** Callback when the takeover is closed */
  onClose?: () => void;
  /** Callback when `displayUntil` is saved */
  onSave?: (displayUntil?: EmbeddedDisplayUntil) => void;
  /** Display until configuration */
  displayUntil?: EmbeddedDisplayUntil;
}

export const DISPLAY_UNTIL_TAKEOVER_ID = 'display-until-takeover';
export const DISPLAY_UNTIL_TAKEOVER_END_DATE_BUTTON_ID = 'end-date-button';
export const DISPLAY_UNTIL_TAKEOVER_SEARCH_QUERY_BUTTON_ID = 'search-query-button';
export const DISPLAY_UNTIL_TAKEOVER_SAVE_BUTTON_ID = 'save-button';

const DisplayUntilTakeover = ({
  onClose = noop,
  onSave = noop,
  displayUntil: originalDisplayUntil,
}: DisplayUntilTakeoverProps) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);

  const [isConfirmModalShowing, setIsConfirmModalShowing] = useState<boolean>(false);
  const [displayUntil, setDisplayUntil] = useState<EmbeddedDisplayUntil | undefined>(
    originalDisplayUntil,
  );
  const [errorsAreVisible, setErrorsAreVisible] = useState(false);
  const { endDate, searchQuery } = displayUntil || {};

  const queryErrors = useMemo(() => getQueryErrors(searchQuery), [searchQuery?.searchQueries?.[0]]);

  /** Update *INTERNAL* `displayUntil` value */
  const updateDisplayUntil = (newDisplayUntil?: EmbeddedDisplayUntil) => {
    setDisplayUntil({ ...displayUntil, ...newDisplayUntil });
  };
  /** Updates `displayUntil.endDate` -- convenience shortcut */
  const updateEndDate = (newEndDate?: EmbeddedDisplayUntil['endDate']) =>
    updateDisplayUntil({ endDate: newEndDate });
  /** Updates `displayUntil.searchQuery` -- convenience shortcut */
  const updateSearchQuery = (newSearchQuery?: EmbeddedDisplayUntil['searchQuery']) => {
    updateDisplayUntil({ searchQuery: newSearchQuery });
  };
  /** Handles when a user closes out of the takeover without clicking "Save" */
  const handleClose = () => {
    const hasChanged = !isEqual(originalDisplayUntil, displayUntil);
    if (!hasChanged) {
      onClose();
    } else {
      setIsConfirmModalShowing(true);
    }
  };
  /** Validates and saves */
  const handleSave = () => {
    if (isEmpty(queryErrors)) {
      onSave(displayUntil);
    } else {
      setErrorsAreVisible(true);
    }
  };

  return (
    <>
      <WithdrawnDrawerTakeover
        data-test={DISPLAY_UNTIL_TAKEOVER_ID}
        withPortal
        isOverlay
        onClose={handleClose}
        HeaderBarContent={<TakeoverHeaderBarContent>Display until</TakeoverHeaderBarContent>}
        Body={
          <>
            <Body2>This message will display until any of the following occurs. </Body2>
            <DisplayUntilItem>Journey is off or archived</DisplayUntilItem>
            {!!endDate && <EndDateItem endDate={endDate} onChange={updateEndDate} />}
            {!!searchQuery && (
              <PropertyChangeItem
                searchQuery={searchQuery}
                errors={errorsAreVisible ? queryErrors : {}}
                onChange={updateSearchQuery}
              />
            )}
            <ButtonRow>
              {!endDate && (
                <AddItemButton
                  data-test={DISPLAY_UNTIL_TAKEOVER_END_DATE_BUTTON_ID}
                  onClick={() =>
                    updateEndDate(
                      formatInTimeZone(
                        addHours(new Date(), 24),
                        projectTimeZone,
                        "yyyy-MM-dd'T'HH:mm:ssXXX",
                      ),
                    )
                  }
                >
                  Specific date
                </AddItemButton>
              )}
              {!searchQuery && (
                <AddItemButton
                  data-test={DISPLAY_UNTIL_TAKEOVER_SEARCH_QUERY_BUTTON_ID}
                  // TODO: Consolidate enums to avoid ts errors
                  // @ts-ignore
                  onClick={() => updateSearchQuery(SEARCH_QUERY_STUB)}
                >
                  User field changes
                </AddItemButton>
              )}
            </ButtonRow>
          </>
        }
        Footer={
          <FooterWrapper>
            <Button
              data-test={DISPLAY_UNTIL_TAKEOVER_SAVE_BUTTON_ID}
              variant={BUTTON_VARIANTS.PRIMARY}
              onClick={handleSave}
            >
              Save
            </Button>
          </FooterWrapper>
        }
      />
      {isConfirmModalShowing && (
        <ConfirmationModal
          onClose={() => setIsConfirmModalShowing(false)}
          onConfirm={handleSave}
          onDiscard={onClose}
        />
      )}
    </>
  );
};

export default DisplayUntilTakeover;
